const SVG_NS = 'http://www.w3.org/2000/svg';

const DEFAULT_PALETTE = [
  'var(--ew-ui-accent)',
  'var(--ew-ui-code-kw)',
  'var(--ew-ui-code-str)',
  'var(--ew-ui-code-num)',
  'var(--ew-ui-code-tag)',
  'var(--ew-ui-code-key)'
];

export function createChartFrame(node, fallbackTitle) {
  const root = document.createElement('div');
  root.className = 'ew-chart';

  const header = document.createElement('div');
  header.className = 'ew-node-header';
  header.dataset.ewDrag = 'true';
  header.dataset.ewSelect = 'true';
  header.dataset.ewLink = 'true';

  const title = document.createElement('span');
  title.textContent = resolveTitle(node, fallbackTitle);

  const anchor = document.createElement('span');
  anchor.className = 'ew-link-anchor';
  anchor.dataset.ewLink = 'true';
  anchor.title = 'Link';

  header.append(title, anchor);

  const body = document.createElement('div');
  body.className = 'ew-node-body ew-chart-body';

  const svg = createSvg();
  const legend = document.createElement('div');
  legend.className = 'ew-chart-legend';

  body.append(svg, legend);
  root.append(header, body);

  return { root, header, body, svg, legend, titleEl: title };
}

export function resolveTitle(node, fallbackTitle) {
  return node?.props?.title || node?.data?.title || fallbackTitle || 'Chart';
}

export function resolvePalette(node) {
  const palette = node?.props?.palette || node?.data?.palette;
  if (Array.isArray(palette) && palette.length) return palette;
  return DEFAULT_PALETTE;
}

export function createSvg(width = 320, height = 180) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.classList.add('ew-chart-svg');
  return svg;
}

export function clearSvg(svg) {
  while (svg.firstChild) svg.removeChild(svg.firstChild);
}

export function getLayout({ width = 320, height = 180, pad = 24, padTop = 20, padRight = 24, padBottom = 22, padLeft = 24 } = {}) {
  const inner = {
    x: padLeft,
    y: padTop,
    w: Math.max(0, width - padLeft - padRight),
    h: Math.max(0, height - padTop - padBottom)
  };
  return { width, height, inner };
}

export function appendGrid(svg, layout, { rows = 4, cols = 4 } = {}) {
  const { inner } = layout;
  for (let r = 1; r < rows; r += 1) {
    const y = inner.y + (inner.h * r) / rows;
    const line = createSvgEl('line', {
      x1: inner.x,
      y1: y,
      x2: inner.x + inner.w,
      y2: y,
      stroke: 'var(--ew-ui-panel-border)',
      'stroke-opacity': '0.35'
    });
    line.classList.add('ew-chart-grid');
    svg.appendChild(line);
  }
  for (let c = 1; c < cols; c += 1) {
    const x = inner.x + (inner.w * c) / cols;
    const line = createSvgEl('line', {
      x1: x,
      y1: inner.y,
      x2: x,
      y2: inner.y + inner.h,
      stroke: 'var(--ew-ui-panel-border)',
      'stroke-opacity': '0.35'
    });
    line.classList.add('ew-chart-grid');
    svg.appendChild(line);
  }
}

export function appendAxes(svg, layout) {
  const { inner } = layout;
  const axisX = createSvgEl('line', {
    x1: inner.x,
    y1: inner.y + inner.h,
    x2: inner.x + inner.w,
    y2: inner.y + inner.h,
    stroke: 'var(--ew-ui-accent)',
    'stroke-opacity': '0.8'
  });
  axisX.classList.add('ew-chart-axis');

  const axisY = createSvgEl('line', {
    x1: inner.x,
    y1: inner.y,
    x2: inner.x,
    y2: inner.y + inner.h,
    stroke: 'var(--ew-ui-accent)',
    'stroke-opacity': '0.8'
  });
  axisY.classList.add('ew-chart-axis');

  svg.append(axisX, axisY);
}

export function addLabel(svg, { x, y, text, anchor = 'middle', className = 'ew-chart-label' } = {}) {
  const label = createSvgEl('text', {
    x,
    y,
    'text-anchor': anchor,
    fill: 'var(--ew-ui-text)',
    'font-size': '10px'
  });
  if (className) {
    String(className)
      .split(/\s+/)
      .filter(Boolean)
      .forEach((token) => label.classList.add(token));
  }
  label.textContent = text;
  svg.appendChild(label);
  return label;
}

export function renderLegend(legendEl, series, { show = true } = {}) {
  if (!legendEl) return;
  if (!show || !Array.isArray(series) || series.length <= 1) {
    legendEl.style.display = 'none';
    legendEl.innerHTML = '';
    return;
  }
  legendEl.style.display = 'grid';
  legendEl.innerHTML = '';
  series.forEach((entry, i) => {
    const item = document.createElement('div');
    item.className = 'ew-chart-legend-item';
    const swatch = document.createElement('span');
    swatch.className = 'ew-chart-swatch';
    swatch.style.background = entry.color || DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];
    const label = document.createElement('span');
    label.textContent = entry.name || `Series ${i + 1}`;
    item.append(swatch, label);
    legendEl.appendChild(item);
  });
}

export function createSvgEl(tag, attrs = {}) {
  const el = document.createElementNS(SVG_NS, tag);
  Object.entries(attrs).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    el.setAttribute(key, String(value));
  });
  return el;
}

export function scaleLinear(value, min, max, outMin, outMax) {
  if (!Number.isFinite(value)) return outMin;
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return (outMin + outMax) / 2;
  const t = (value - min) / (max - min);
  return outMin + t * (outMax - outMin);
}

export function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function clamp(value, min, max) {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export function normalizeSeriesColors(series, palette) {
  return series.map((entry, i) => ({
    ...entry,
    color: entry.color || palette[i % palette.length]
  }));
}
